import { Controller } from "../../lib/stimulus-shim"

/**
 * profiles--webmcp-discovery controller
 *
 * Enregistre les tools WebMCP sur les pages publiques.
 * Le mode determine les tools exposes :
 *
 *   mode="strategy" (landing + homepage) :
 *     - discover_concept, suggest_for_activity, check_address_availability
 *     - Tunnel de conversion : comprendre → choisir → verifier
 *
 *   mode="explore" (pages explore) :
 *     - find_X (polymorphe par verbe) — recherche profils
 *     - + les 3 Strategy tools
 *
 * Pages cibles :
 *   - rholala.la/ (homepage) — mode=strategy, pas de verb
 *   - achete.la/ (landing) — mode=strategy, verb="achete"
 *   - /explore, achete.la/explore — mode=explore, verb dynamique
 */

const VERB_TOOLS = {
  achete:  { name: "find_seller",   description: "Search for sellers, shops, and products on this marketplace." },
  regarde: { name: "find_creator",  description: "Search for video creators, visual artists, and influencers showcasing their work." },
  ecoute:  { name: "find_artist",   description: "Search for musicians, podcasters, and audio creators." },
  reserve: { name: "find_provider", description: "Search for service providers available for booking and appointments." },
}

const DEFAULT_TOOL = { name: "find_profile", description: "Search for profiles by name, specialty, or topic." }

export default class extends Controller {
  static values = {
    verb: String,
    mode: { type: String, default: "strategy" },
    traceUrl: { type: String, default: "/api/mcp/trace" }
  }

  connect() {
    if (!navigator.modelContext) return

    this._registeredToolNames = []
    this._registerTools()
  }

  disconnect() {
    if (navigator.modelContext) {
      this._registeredToolNames.forEach(name => {
        try { navigator.modelContext.unregisterTool?.(name) } catch (e) {}
      })
    }
    this._registeredToolNames = []
  }

  _registerTools() {
    const verb = this.verbValue
    const mode = this.modeValue
    const tools = [
      ...this._strategyTools(verb)
    ]

    // Tool de recherche profils — seulement en mode explore
    if (mode === "explore" && verb) {
      tools.unshift(this._searchTool(verb))
    }

    try {
      tools.forEach(tool => {
        navigator.modelContext.registerTool(tool)
        this._registeredToolNames.push(tool.name)
      })
    } catch (e) {
      console.warn("[WebMCP Discovery] Failed to register tools:", e)
    }
  }

  // === Tool 1 : recherche profils (find_seller, find_creator, etc.) ===

  _searchTool(verb) {
    const toolDef = VERB_TOOLS[verb] || DEFAULT_TOOL

    return {
      name: toolDef.name,
      description: toolDef.description,
      inputSchema: {
        type: "object",
        properties: {
          query: {
            type: "string",
            description: "Search query to find by name, specialty, or topic"
          },
          limit: {
            type: "number",
            description: "Maximum number of results (default 10, max 20)"
          }
        },
        required: ["query"]
      },
      execute: async (input) => {
        this._trace("_discovery", toolDef.name, verb)

        const params = new URLSearchParams({
          q: input.query || "",
          verb: verb,
          limit: Math.min(input.limit || 10, 20)
        })

        const response = await fetch(`/api/mcp/experts/search?${params}`)
        return response.json()
      },
      annotations: { readOnlyHint: true }
    }
  }

  // === Tools 2-4 : Strategy (discover → suggest → check) ===

  _strategyTools(verb) {
    return [
      {
        name: "discover_concept",
        description: verb
          ? `Discover the "${verb}.la" concept: what it is, how it works, and who it's for.`
          : "Discover the Rholala link-verb concept: 4 specialized domains (achete.la, regarde.la, ecoute.la, reserve.la), how they work, and who they're for.",
        inputSchema: {
          type: "object",
          properties: {
            verb: {
              type: "string",
              description: "Optional verb to focus on (achete, regarde, ecoute, reserve)"
            }
          }
        },
        execute: async (input) => {
          const v = input.verb || verb || ""
          this._trace("strategy", "discover_concept", v)

          const params = v ? `?verb=${encodeURIComponent(v)}` : ""
          const response = await fetch(`/api/mcp/strategy/discover${params}`)
          return response.json()
        },
        annotations: { readOnlyHint: true }
      },
      {
        name: "suggest_for_activity",
        description: "Suggest the best verb domain and modules for a given professional activity or business type.",
        inputSchema: {
          type: "object",
          properties: {
            activity: {
              type: "string",
              description: "The professional activity or business type (e.g. 'photographe', 'restaurant', 'coach sportif')"
            },
            verb: {
              type: "string",
              description: "Optional verb preference (achete, regarde, ecoute, reserve)"
            }
          },
          required: ["activity"]
        },
        execute: async (input) => {
          const v = input.verb || verb || ""
          this._trace("strategy", "suggest_for_activity", v)

          const params = new URLSearchParams({ activity: input.activity })
          if (v) params.set("verb", v)

          const response = await fetch(`/api/mcp/strategy/suggest?${params}`)
          return response.json()
        },
        annotations: { readOnlyHint: true }
      },
      {
        name: "check_address_availability",
        description: "Check if a slug/address is available on a verb domain (e.g. achete.la/mon-slug).",
        inputSchema: {
          type: "object",
          properties: {
            slug: {
              type: "string",
              description: "The desired slug/address to check (e.g. 'jean-photo', 'ma-boutique')"
            },
            verb: {
              type: "string",
              description: "The verb domain to check on (achete, regarde, ecoute, reserve)"
            }
          },
          required: ["slug", "verb"]
        },
        execute: async (input) => {
          const v = input.verb || verb
          if (!v) return { error: "Le paramètre 'verb' est requis." }

          this._trace("strategy", "check_address_availability", v)

          const params = new URLSearchParams({ slug: input.slug, verb: v })
          const response = await fetch(`/api/mcp/strategy/check_address_availability?${params}`)
          return response.json()
        },
        annotations: { readOnlyHint: true }
      }
    ]
  }

  _trace(slug, toolName, verb) {
    const payload = JSON.stringify({
      slug: slug,
      tool_name: toolName,
      verb: verb
    })
    try {
      navigator.sendBeacon(this.traceUrlValue, new Blob([payload], { type: "application/json" }))
    } catch (e) {
      fetch(this.traceUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true
      }).catch(() => {})
    }
  }
}
